import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/lib/siteDil";
import { AramaKutusu, UrunKarti } from "@/components/site/SiteParca";

const Filtre = ({ ic, t, grup, marka, sec }) => (
  <div className="space-y-1" data-testid="site-filtre">
    <button onClick={() => sec({})} data-testid="site-filtre-tum" className={`w-full text-start px-3 py-2 rounded-sm text-sm font-semibold ${!grup && !marka ? "bg-[var(--s-primary)] text-white" : "hover:bg-slate-100"}`}>{t.tumUrunler}</button>
    {(ic?.gruplar || []).map((g, i) => (
      <div key={g.ad_tr}>
        <button onClick={() => sec({ grup: g.ad_tr })} data-testid={`site-filtre-grup-${i}`}
          className={`w-full flex justify-between gap-2 text-start px-3 py-2 rounded-sm text-sm font-semibold ${grup === g.ad_tr && !marka ? "bg-[var(--s-primary)] text-white" : "text-slate-800 hover:bg-slate-100"}`}>
          <span>{g.ad}</span><span className="text-xs opacity-60">{g.adet}</span></button>
        {grup === g.ad_tr && g.markalar.map((m) => (
          <button key={m.ad} onClick={() => sec({ grup: g.ad_tr, marka: m.ad })} data-testid={`site-filtre-marka-${m.ad.replace(/\s+/g, "-")}`}
            className={`w-full flex justify-between gap-2 text-start ps-7 pe-3 py-1.5 rounded-sm text-sm ${marka === m.ad ? "text-[var(--s-primary)] font-semibold bg-blue-50" : "text-slate-600 hover:bg-slate-50"}`}>
            <span>{m.ad}</span><span className="text-xs opacity-60">{m.adet}</span></button>
        ))}
      </div>
    ))}
  </div>
);

export default function SiteUrunler() {
  const { t, lang, ic } = useSite();
  const [p, setP] = useSearchParams();
  const [d, setD] = useState(null);
  const [mobilFiltre, setMobilFiltre] = useState(false);
  const q = p.get("q") || "", grup = p.get("grup") || "", marka = p.get("marka") || "", sayfa = Number(p.get("sayfa") || 1);
  useEffect(() => {
    setD(null);
    api.get("/site/urunler", { params: { lang, q, grup, marka, sayfa } }).then((r) => setD(r.data)).catch(() => setD({ toplam: 0, urunler: [], sayfa_boyu: 24 }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [lang, q, grup, marka, sayfa]);
  const sec = (x) => { const n = { ...(q ? { q } : {}), ...x }; Object.keys(n).forEach((k) => !n[k] && delete n[k]); setP(n); setMobilFiltre(false); };
  const git = (s) => setP({ ...Object.fromEntries(p), sayfa: String(s) });
  const son = d ? Math.max(1, Math.ceil(d.toplam / d.sayfa_boyu)) : 1;
  const baslik = marka || ic?.gruplar.find((g) => g.ad_tr === grup)?.ad || t.urunler;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="site-urunler">
      <div className="space-y-4">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="site-urunler-baslik">{baslik}</h1>
        <AramaKutusu key={q} baslangic={q} onAra={(v) => sec({ grup, marka, q: v })} />
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-8 items-start">
        <aside className="hidden lg:block sticky top-24"><Filtre ic={ic} t={t} grup={grup} marka={marka} sec={sec} /></aside>
        <div className="space-y-5 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600" data-testid="site-sonuc-sayi">{d ? `${d.toplam} ${t.sonuc}` : "…"}</span>
            <div className="flex gap-2">
              {(q || grup || marka) && <button onClick={() => setP({})} data-testid="site-filtre-temizle" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"><X className="w-4 h-4" />{t.temizle}</button>}
              <button onClick={() => setMobilFiltre(!mobilFiltre)} data-testid="site-filtre-ac" className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-slate-300 text-sm"><SlidersHorizontal className="w-4 h-4" />{t.filtreler}</button>
            </div>
          </div>
          {mobilFiltre && <div className="lg:hidden border border-slate-200 rounded-md p-2"><Filtre ic={ic} t={t} grup={grup} marka={marka} sec={sec} /></div>}
          {d && !d.urunler.length && <p className="py-16 text-center text-slate-500" data-testid="site-sonuc-yok">{t.sonucYok}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">{d?.urunler.map((u, i) => <UrunKarti key={u.id} u={u} i={i} />)}</div>
          {d && d.toplam > d.sayfa_boyu && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <button disabled={sayfa <= 1} onClick={() => git(sayfa - 1)} data-testid="site-onceki" className="inline-flex items-center gap-1 h-10 px-4 rounded-sm border border-slate-300 text-sm disabled:opacity-40"><ChevronLeft className="w-4 h-4 rtl:rotate-180" />{t.onceki}</button>
              <span className="text-sm text-slate-600" data-testid="site-sayfa">{sayfa} / {son}</span>
              <button disabled={sayfa >= son} onClick={() => git(sayfa + 1)} data-testid="site-sonraki" className="inline-flex items-center gap-1 h-10 px-4 rounded-sm border border-slate-300 text-sm disabled:opacity-40">{t.sonraki}<ChevronRight className="w-4 h-4 rtl:rotate-180" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
